import {
  checkEmployeeAvailability,
  toInputValue,
  toIsoString,
  type EmployeeOption,
  type WorkingHoursByUser,
} from "../../../../../../../../_shared/_logic/form-state"
import {
  ceilPositiveMinutesToStep,
  SCHEDULING_MINUTE_STEP,
} from "../../../../../../../../_shared/_logic/time-granularity"

import type { ConfirmationEmailChoice } from "../../../_logic/confirmation-email-choice"

export type RescheduleDraftState = {
  startAt: string
  durationMinutes: number
  employeeId: string
  emailChoice: ConfirmationEmailChoice
}

export const computeEventDurationMinutes = (
  event: Pick<ScheduleEvent, "start_at" | "end_at"> | null | undefined,
  fallbackMinutes?: number | null,
): number => {
  if (event?.start_at && event?.end_at) {
    const start = new Date(event.start_at)
    const end = new Date(event.end_at)
    const diffMinutes = (end.getTime() - start.getTime()) / 60000

    if (Number.isFinite(diffMinutes) && diffMinutes > 0) {
      return ceilPositiveMinutesToStep(diffMinutes, 60) ?? 60
    }
  }

  const fallback = Number(fallbackMinutes ?? 60)

  return ceilPositiveMinutesToStep(fallback > 0 ? fallback : 60, 60) ?? 60
}

export const buildRescheduleEmployeeOptions = ({
  employees,
  workOrder,
  currentEmployeeId,
}: {
  employees: EmployeeOption[]
  workOrder?: WorkOrder | null
  currentEmployeeId?: string | null
}): Array<{ value: string; label: string; name: string }> => {
  const allowedIds =
    workOrder?.allowed_employee_ids?.map((id) => String(id ?? "").trim()).filter(Boolean) ?? []
  const allowedSet = allowedIds.length ? new Set(allowedIds) : null

  if (allowedSet && currentEmployeeId) {
    allowedSet.add(currentEmployeeId)
  }

  const filtered = allowedSet
    ? employees.filter((emp) => allowedSet.has(emp.id))
    : employees
  const effective = filtered.length ? filtered : employees

  return effective.map((emp) => ({
    value: emp.id,
    label: emp.label,
    name: emp.label,
  }))
}

export const createInitialRescheduleDraft = ({
  event,
  workOrder,
  employees,
}: {
  event: ScheduleEvent
  workOrder?: WorkOrder | null
  employees: EmployeeOption[]
}): RescheduleDraftState => {
  const options = buildRescheduleEmployeeOptions({
    employees,
    workOrder,
    currentEmployeeId: event.employee_id,
  })
  const preferredEmployeeId =
    event.employee_id ?? workOrder?.assigned_employee_id ?? options[0]?.value ?? ""
  const resolvedEmployeeId = options.some((opt) => opt.value === preferredEmployeeId)
    ? preferredEmployeeId
    : (options[0]?.value ?? preferredEmployeeId)

  return {
    startAt: toInputValue(event.start_at),
    durationMinutes: computeEventDurationMinutes(event, workOrder?.estimated_duration_min),
    employeeId: resolvedEmployeeId,
    emailChoice: "send",
  }
}

export const computeRescheduleRange = (
  startAtInput: string,
  durationMinutes: number,
): {
  startIso: string
  endIso: string
  startDate: Date
  endDate: Date
  normalizedDurationMinutes: number
} | null => {
  const startIso = toIsoString(startAtInput)

  if (!startIso) return null

  const startDate = new Date(startIso)

  if (Number.isNaN(startDate.getTime())) return null

  const normalizedDurationMinutes =
    ceilPositiveMinutesToStep(durationMinutes, SCHEDULING_MINUTE_STEP) ?? SCHEDULING_MINUTE_STEP

  if (!Number.isFinite(normalizedDurationMinutes) || normalizedDurationMinutes <= 0) return null

  const endDate = new Date(startDate.getTime() + normalizedDurationMinutes * 60000)

  return {
    startIso: startDate.toISOString(),
    endIso: endDate.toISOString(),
    startDate,
    endDate,
    normalizedDurationMinutes,
  }
}

export type RescheduleDraftValidationResult =
  | {
      ok: true
      updatePayload: ScheduleEventUpdatePayload
      sendConfirmationEmail: boolean
    }
  | {
      ok: false
      error?: string | null
    }

export const validateRescheduleDraft = ({
  draft,
  event,
  employeeOptions,
}: {
  draft: RescheduleDraftState
  event: ScheduleEvent
  employeeOptions: Array<{ value: string }>
}): RescheduleDraftValidationResult => {
  const validEmployeeIds = new Set(employeeOptions.map((opt) => opt.value).filter(Boolean))

  if (!draft.employeeId || (validEmployeeIds.size > 0 && !validEmployeeIds.has(draft.employeeId))) {
    return { ok: false, error: "Bitte einen Mitarbeiter auswählen." }
  }

  const range = computeRescheduleRange(draft.startAt, draft.durationMinutes)

  if (!range) {
    return { ok: false, error: "Bitte einen gültigen Zielzeitpunkt und eine gültige Dauer wählen." }
  }

  const currentStartMs = new Date(event.start_at).getTime()
  const currentEndMs = new Date(event.end_at).getTime()
  const sameStart =
    Number.isFinite(currentStartMs) && range.startDate.getTime() === currentStartMs
  const sameEnd = Number.isFinite(currentEndMs) && range.endDate.getTime() === currentEndMs
  const sameEmployee = draft.employeeId === (event.employee_id ?? "")

  if (sameStart && sameEnd && sameEmployee) {
    return {
      ok: false,
      error: null,
    }
  }

  return {
    ok: true,
    updatePayload: {
      employee_id: draft.employeeId,
      flex_type: "HARD",
      start_at: range.startIso,
      end_at: range.endIso,
      timeframe_start_at: range.startIso,
      timeframe_end_at: range.endIso,
    },
    sendConfirmationEmail: draft.emailChoice === "send",
  }
}

export const evaluateRescheduleAvailabilityHint = ({
  draft,
  event,
  scheduleEvents = [],
  workingHoursByUser,
}: {
  draft: RescheduleDraftState
  event: ScheduleEvent
  scheduleEvents?: ScheduleEvent[]
  workingHoursByUser?: WorkingHoursByUser
}): string | null => {
  if (!draft.employeeId) return null

  const range = computeRescheduleRange(draft.startAt, draft.durationMinutes)

  if (!range) return null

  const availability = checkEmployeeAvailability({
    employeeId: draft.employeeId,
    start: range.startDate,
    end: range.endDate,
    scheduleEvents,
    workingHoursByUser,
    currentEventId: event.id,
    eventType: "APPOINTMENT",
    flexType: "HARD",
  })

  if (!availability.ok) {
    return availability.reason ?? null
  }

  return null
}

